import {Injectable} from '@angular/core';
import * as monaco from 'monaco-editor';
import {NgxMonacoEditorConfig} from "ngx-monaco-editor-v2";
import {WindowPropertyInitializer} from "../initialization/window-property.initializer";
import {MonacoEditorComponent} from "../../components/monaco-editor/monaco-editor.component";
import {FeatureFlagsService} from "../feature-flag/feature-flags.service";

// Support for filtering completion items
import {StandaloneServices} from "monaco-editor/esm/vs/editor/standalone/browser/standaloneServices";
import {ILanguageFeaturesService} from "monaco-editor/esm/vs/editor/common/services/languageFeatures";
import Uri = monaco.Uri;
import {languages} from "monaco-editor";
import CompletionItemKind = languages.CompletionItemKind;

interface TypeDefinition {
  name: string,
  path?: string | string[],
  content?: string,
  replace?: boolean
}

@Injectable({
  providedIn: 'root'
})
export class MonacoEditorService {

  // Make this load schemas from somewhere
  jsonSchemaCache = {

    'http://myserver/foo-schema.json': {
      uri: 'http://myserver/foo-schema.json',
      fileMatch: ['*.foo.json'], // Arbitrary name to associate with a file
      schema: {
        type: 'object',
        properties: {
          p1: {
            enum: ['v1', 'v2']
          },
          p2: {
            $ref: 'http://myserver/bar-schema.json' // reference to another schema
          }
        }
      }
    },

    'http://myserver/bar-schema.json': {
      uri: 'http://myserver/bar-schema.json',
      schema: {
        type: 'object',
        properties: {
          q1: {
            enum: ['x1', 'x2']
          }
        }
      }
    }

  };

  knownJavaScriptDefinitions: {
    [key: string]: string | string[]
  } = {
    'globals': '/scripts/assets/monaco/types/globals/globals.d.ts',
    'localize': '/scripts/assets/monaco/types/globals/localize.d.ts',
    'moment': '/scripts/assets/monaco/types/moment/moment.d.ts',
    'jquery': [
      '/scripts/assets/monaco/types/jquery/JQueryStatic.d.ts',
      '/scripts/assets/monaco/types/jquery/JQuery.d.ts',
      '/scripts/assets/monaco/types/jquery/misc.d.ts',
      '/scripts/assets/monaco/types/jquery/legacy.d.ts',
    ],
    'client-script': '/dts/client-script/all'
  };

  knownTypeScriptDefinitions: {
    [key: string]: string | string[]
  } = {
    'globals': '/scripts/assets/monaco/types/globals/globals.d.ts',
    'localize': '/scripts/assets/monaco/types/globals/localize.d.ts',
    'angular.core': '/scripts/assets/monaco/types/angular/index.d.ts',
    'highcharts': '/scripts/assets/monaco/types/highcharts/highcharts.d.ts',
    'moment': '/scripts/assets/monaco/types/moment/moment.d.ts',
    'jquery': [
      '/scripts/assets/monaco/types/jquery/JQueryStatic.d.ts',
      '/scripts/assets/monaco/types/jquery/JQuery.d.ts',
      '/scripts/assets/monaco/types/jquery/misc.d.ts',
      '/scripts/assets/monaco/types/jquery/legacy.d.ts',
      ],
    'portal-base.component': '/scripts/assets/monaco/types/servicely/app/core/components/portal-base/portal-base.component.d.ts',
    'portal.service': '/scripts/assets/monaco/types/servicely/app/core/services/portal/portal.service.d.ts',
    'cmdb.service': '/scripts/assets/monaco/types/servicely/app/core/services/cmdb/cmdb.service.d.ts',
    'portal-page.model': '/scripts/assets/monaco/types/servicely/app/core/services/portal-data/portal-page.model.d.ts',
    'dashboard.model': '/scripts/assets/monaco/types/servicely/app/core/services/portal-data/dashboard.model.d.ts',
    'report-dashboard.model': '/scripts/assets/monaco/types/servicely/app/core/services/portal-data/report-dashboard.model.d.ts',
    'portal-component.model': '/scripts/assets/monaco/types/servicely/app/core/services/portal-data/portal-component.model.d.ts',
    'catalog-data.service': '/scripts/assets/monaco/types/servicely/app/core/services/catalog-data/catalog-data.service.d.ts',
    'catalog-navigation.service': '/scripts/assets/monaco/types/servicely/app/pages/catalog-page/services/catalog-navigation/catalog-navigation.service.d.ts',
    'portal-crumb': '/scripts/assets/monaco/types/servicely/app/core/services/portal/portal-crumb.d.ts',
    'schedule-resource': '/scripts/assets/monaco/types/servicely/app/pages/schedule-page/schedule-resource.model.d.ts',
    'schedule-event': '/scripts/assets/monaco/types/servicely/app/pages/schedule-page/schedule-event.model.d.ts',
    'pivotview': '/scripts/assets/monaco/types/syncfusion/pivotview/datasourcesettings-model.d.ts',
    'server-script': '/dts/server-script/all',
    'field-service': '/scripts/assets/monaco/types/servicely/app/core/services/field/field.service.d.ts',
    'knockout-field.model': '/scripts/assets/monaco/types/servicely/app/core/components/knockout-field/knockout-field.model.d.ts',
    'knockout-field-data.model': '/scripts/assets/monaco/types/servicely/app/core/components/knockout-field/knockout-field-data.model.d.ts',
  };

  javaScriptDefinitionCache: any = {
  };

  typeScriptDefinitionCache: any = {
  };

  editors: MonacoEditorComponent[] = [];

  static defaultConfig(): NgxMonacoEditorConfig {
    return {
      defaultOptions: {
        scrollBeyondLastLine: false
      },
      onMonacoLoad: () => {
        // Define some themes to use aside from the standard 'vs' one
        fetch('/scripts/assets/monaco/themes/Chrome DevTools.json')
          .then(res => res.json())
          .then (res => {
            monaco.editor.defineTheme('chrome', res);
            monaco.editor.setTheme('chrome');
          });

        // Turn on all validation for JavaScript
        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(
          {
            noSemanticValidation: false,
            noSyntaxValidation: false,
            noSuggestionDiagnostics: false,
          }
        );

        // Turn on all validation for TypeScript
        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(
          {
            noSemanticValidation: false,
            noSyntaxValidation: false,
            noSuggestionDiagnostics: false
          }
        );

      }
    };
  }

  constructor(private windowPropertyInitializer: WindowPropertyInitializer,
              private featureFlagsService: FeatureFlagsService) {
    (window as any).NG_SHARED_MONACO_EDITOR_SERVICE = this;
  }

  // Callers of this function should await monacoReady() before using it.
  uriParse(uri: string): Uri {
    return monaco.Uri.parse(uri);
  }

  async monacoReady(): Promise<boolean> {
    if ((window as any).monaco) {
      return Promise.resolve(true);
    }
    await this.windowPropertyInitializer.wait(['monaco'], 'Monaco');
    return Promise.resolve(true);
  }

  registerEditor(editor: MonacoEditorComponent) {
    this.editors.push(editor);
  }

  deregisterEditor(editor: MonacoEditorComponent) {
    const idx = this.editors.indexOf(editor);
    idx >= 0 && this.editors.splice(idx, 1);
  }

  focusEditor(editor: MonacoEditorComponent) {
    editor.configureForEditor();
  }

  blurEditor(editor: MonacoEditorComponent) {
    editor.deconfigureForEditor();
  }

  // Enable hidden editors from a parent element
  enableEditors(parentContainer: any) {
    this.editors.forEach((editor) => {
      if (editor.editor && editor.editor.layout) {
        editor.editor.layout();
      }
    });
  }

  getTypeScriptCache(): any {
    return this.typeScriptDefinitionCache;
  }

  getJavaScriptCache(): any {
    return this.javaScriptDefinitionCache;
  }

  async init(): Promise<any> {
    return {};
  }

  loadJavaScriptTypeDefinition(def: TypeDefinition) {
    return this.loadTypeDefinition(def, this.javaScriptDefinitionCache);
  }

  loadTypeScriptTypeDefinition(def: TypeDefinition) {
    return this.loadTypeDefinition(def, this.typeScriptDefinitionCache);
  }

  async configureForSql() {
    await this.monacoReady();
  }

  async configureForText() {
    await this.monacoReady();
  }

  async configureForXml() {
    await this.monacoReady();
  }

  async configureForHtml() {
    await this.monacoReady();
  }

  async configureForScss() {
    await this.monacoReady();
  }

  async configureForCss() {
    await this.monacoReady();
  }

  /**
   * Configure for a JSON file
   * @param schemas the schemas that the file should conform to
   * @param uri the file uri that indicates the top schema
   */
  async configureForJson(schemas: string[] = [], uri: string = "") {
    await this.monacoReady();
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions(
      {
        validate: true,
        schemas: schemas.map((key) => this.jsonSchemaCache[key])
      });
  }

  async configureForJavaScript() {
    await this.monacoReady();
    // Set compiler options for a browser
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
      {
        target: 2, // ES2015
        allowJs: true,
        allowNonTsExtensions: true,
        alwaysStrict: true,
        noImplicitAny: true
      }
    );
  }

  /**
   * Configure for a general TypeScript file
   */
  async configureForTypeScript() {
    await this.monacoReady();
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions(
      {
        target: 3, // ES2016
        allowJs: true,
        allowNonTsExtensions: true,
        experimentalDecorators: true,
        alwaysStrict: true,
        noImplicitAny: true
      }
    );
  }

  /**
   * Configure for a server script
   * @param globals a list of global variables available in the script
   * @param libraries other libraries that are accessible
   * @param editContext the table.field that this editor is for
   */
  async configureForServerScript(globals: string[] = [], libraries: string[] = [], editContext: string = null) {
    await this.monacoReady();

    // server-scripts can be written in TypeScript and get transpiled for Rhino
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions(
      {
        target: 2, // ES2015
        allowJs: true,
        allowNonTsExtensions: true,
        experimentalDecorators: true,
        alwaysStrict: false,
        noImplicitAny: false,
        lib: ['es2015'], // Gets rid of DOM and browser intellisense
        // lib: [], // Gets rid of DOM and browser intellisense
      }
    );

    // As we don't have definitions for a lot of types we need to ignore this error for now.
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      diagnosticCodesToIgnore: [2304, 7044]
    });

    const defaultLibraries = [
      "moment",
      "server-script",
    ];

    const globalDefs = this.defineGlobals(globals);

    const libs = await this.loadTypeScriptTypeDefinitions(libraries);
    const defaultLibs = await this.loadTypeScriptTypeDefinitions(defaultLibraries);

    const scopeSignatures = await this.loadTypeScriptTypeDefinitions([`editor-scope:/dts/server-script-scope/${editContext}`], false); // Should replace be true?

    monaco.languages.typescript.typescriptDefaults.setExtraLibs(
      [...defaultLibs, ...libs, ...scopeSignatures, { content: globalDefs, filePath: 'ts:filename/editor-globals.d.ts' }]
    );

    // Filter the completion items to hide the standard function functions. This is because we
    // only want completions that are Servicely Function or Relation wrapper functions, like 'hasValue'
    let languageFeaturesServiceId = ILanguageFeaturesService;
    let standaloneServices = StandaloneServices;
    let languageFeaturesService = standaloneServices.get(languageFeaturesServiceId);
    let completionProvider = languageFeaturesService.completionProvider;

    function patchSuggestAdaptor(completionProvider: any) {
      let suggestAdapter = completionProvider._entries.find(x => x?.provider?.constructor?.name === 'SuggestAdapter')?.provider;
      if (suggestAdapter) {
        if (!suggestAdapter.provideCompletionItems.patched) {
          const originalProvideCompletionItems = suggestAdapter.provideCompletionItems;
          suggestAdapter.provideCompletionItems =
            async function provideCompletionItems(...args) {

              const result = await originalProvideCompletionItems.apply(this, args);

              if (result) {
                const functionFunctions = ['apply', 'bind', 'call', 'toString'];
                const functionFields = ['arguments', 'length', 'name', 'caller', 'prototype'];

                result.suggestions = result.suggestions.filter((r) => {
                  return (!functionFunctions.includes(r.label) && r.kind === CompletionItemKind.Function) ||
                    (!functionFields.includes(r.label) && r.kind === CompletionItemKind.Field);
                });
              }
              return result;
            };
          suggestAdapter.provideCompletionItems.patched = true;
        }
      }
    }

    // We cannot patch the "suggest adaptor" until it exists.
    // Sometimes it exists before we get here and the onDidChange does not fire.
    patchSuggestAdaptor(completionProvider);
    completionProvider.onDidChange((_) => {
      patchSuggestAdaptor(completionProvider);
    });
  }

  /**
   * Configure for a client script
   * @param globals a list of global variables available in the script
   * @param libraries other libraries that are accessible
   * @param editContext the table.field that this editor is for
   */
  async configureForClientScript(globals: string[] = [], libraries: string[] = [], editContext: string = null) {
    await this.monacoReady();

    monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
      {
        target: 3, // ES2016
        allowJs: true,
        allowNonTsExtensions: true,
        alwaysStrict: true,
        noImplicitAny: true,
      }
    );

    // As we don't have definitions for a lot of types we need to ignore this error for now.
    // TODO: check this
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      diagnosticCodesToIgnore: [2304, 7044]
    });

    const defaultLibraries = [
      "globals",
      "localize",
      "moment",
      "jquery",
      "client-script",
    ];

    const globalDefs = this.defineGlobals(globals);

    const libs = await this.loadJavaScriptTypeDefinitions(libraries);
    const defaultLibs = await this.loadJavaScriptTypeDefinitions(defaultLibraries);

    monaco.languages.typescript.javascriptDefaults.setExtraLibs(
      [...defaultLibs, ...libs, {
        content: globalDefs,
        filePath: 'ts:filename/editor-globals.d.ts'
      }]
    );
  }

  /**
   * Configure for an Angular template file
   */
  async configureForAngularTemplate() {
    await this.monacoReady();
  }

  /**
   * Configure for an Angular component file
   */
  async configureForAngularComponent() {
    await this.monacoReady();
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions(
      {
        target: 3, // ES2016
        allowJs: true,
        allowNonTsExtensions: true,
        experimentalDecorators: true,
        alwaysStrict: true,
        noImplicitAny: true
      }
    );

    const globals = [];

    const libraries = [
      "globals",
      "localize",
      "angular.core",
      "portal-base.component",
      "portal.service",
      "cmdb.service",
      "portal-page.model",
      "dashboard.model",
      "report-dashboard.model",
      "portal-component.model",
      "catalog-data.service",
      "catalog-navigation.service",
      "portal-crumb",
      "field-service",
      "knockout-field.model",
      "knockout-field-data.model",
      "moment",
      "jquery",
    ];

    const globalDefs = this.defineGlobals(globals);

    const libs = await this.loadTypeScriptTypeDefinitions(libraries);

    monaco.languages.typescript.typescriptDefaults.setExtraLibs(
      [...libs, { content: globalDefs, filePath: 'ts:filename/editor-globals.d.ts' }]
    );
  }

  async configureForAngularScss() {
    await this.monacoReady();
  }

  async loadServerScriptEditorScope(editContext: string) {
    const p = `/dts/server-script-scope-data/${editContext}`;
    const response = await fetch(p);
    if (response.ok) {
      return await response.json();
    } else {
      console.error(`MonacoEditorService.loadServerScriptEditorScope error "${p}" (${response.status})`);
      return [];
    }
  }

  private defineGlobals(globals: string[] = []) {
    return globals.map((e) => {
      const es = e.split(/:/);
      return `declare var ${es[0]}: ${es[1]};`;
    }).join('\n');
  }

  private async loadJavaScriptTypeDefinitions(libraries: string[], replace: boolean = false) {
    const promises: Promise<any>[] = libraries.map(lib => {
      const parts = lib.split(':');
      const name = parts[0];
      const path = parts[1] || this.knownJavaScriptDefinitions[name];
      return this.loadJavaScriptTypeDefinition({name: lib, path: path, replace: replace});
    });

    await Promise.all(promises);

    return libraries.map((name) => {
      return { content: this.javaScriptDefinitionCache[name], filePath: `ts:filename/${name}.d.ts` }
    });
  }

  private async loadTypeScriptTypeDefinitions(libraries: string[], replace: boolean = false) {
    const promises: Promise<any>[] = libraries.map(lib => {
      const parts = lib.split(':');
      const name = parts[0];
      const path = parts[1] || this.knownTypeScriptDefinitions[name];
      return this.loadTypeScriptTypeDefinition({name: name, path: path, replace: replace});
    });

    await Promise.all(promises);

    return libraries.map((lib) => {
      const parts = lib.split(':');
      const name = parts[0];
      return { content: this.typeScriptDefinitionCache[name], filePath: `ts:filename/${name}.d.ts` }
    });
  }

  private async loadTypeDefinition(def: TypeDefinition, cache: any) {
    if (!cache[def.name] || def.replace) {
      if (def.path) {
        if (Array.isArray(def.path)) {
          const promises =  def.path.map(async (p) => {
              const response = await fetch(p);
              if (response.ok) {
                const text = await response.text();
                return this.processTypeDefinitionFile(text);
              } else {
                console.error(`MonacoEditorService.loadTypeDefinition error ${def.name} "${p}" (${response.status})`);
                return "";
              }
          });
          const texts = await Promise.all(promises);
          cache[def.name] = texts.join("\n");
        } else {
            const response = await fetch(def.path);
            if (response.ok) {
              const contentType = response.headers.get('Content-Type');
              if (contentType == "application/json") {
                const map = await response.json();
                const text = Object.values(map).join("\n");
                cache[def.name] = this.processTypeDefinitionFile(text);
              } else {
                const text = await response.text();
                cache[def.name] = this.processTypeDefinitionFile(text);
              }
            } else {
              console.error(`MonacoEditorService.loadTypeDefinition error ${def.name} "${def.path}" (${response.status})`);
            }
        }
      } else if (def.content) {
        cache[def.name] = this.processTypeDefinitionFile(def.content);
        return Promise.resolve();
      }
    } {
      return Promise.resolve();
    }
  }

  // Imports and exports in the file mean that the editor does not parse it for type definitions properly.
  // An inline sourceMapping is just a waste of space for the editor and for some files is 50% of everything.
  private processTypeDefinitionFile(content: string) {
    const removeExport1 = (s: string) => s.replace(/export declare/g, 'declare');

    const removeExport2 = (s: string) => s.replace(/export interface/g, 'interface');

    const removeExport3 = (s: string) => s.replace(/export {/g, '// export {');

    const removeExport4 = (s: string) => s.replace(/export =/g, '// export =');

    const removeExport5 = (s: string) => s.replace(/export function/g, 'function');

    const removeImport = (s: string) => s.replace(/^import/gm, '// import');

    const removeSourceMapping = (s: string) => {
      const i = s.indexOf('//# sourceMappingURL');
      return i >= 0 ? s.substring(0, i) : s;
    };

    const transformations = [
      removeSourceMapping,
      removeExport1,
      removeExport2,
      removeExport3,
      removeExport4,
      removeExport5,
      removeImport
    ];

    return transformations.reduce((curr, next) => {
      curr = next(curr);
      return curr;
    }, content);
  }

}
